import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import ProductCard from "@/components/ProductCard";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import api from "@/lib/api";
import { Filter, SlidersHorizontal, Search, X } from "lucide-react";
import KlarnaBanner from "@/components/KlarnaBanner";
import SEO from "@/components/SEO";
import { useLang } from "@/context/LanguageContext";

export default function Catalog({ accessoriesOnly = false }) {
  const { lang } = useLang();
  const [params, setParams] = useSearchParams();
  const [brands, setBrands] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [priceMax, setPriceMax] = useState(Number(params.get("max_price") || 1200));
  const [sort, setSort] = useState(params.get("sort") || "popular");
  const [screens, setScreens] = useState(params.get("screen") ? [params.get("screen")] : []);
  const [selectedBrand, setSelectedBrand] = useState(params.get("brand") || "");
  const [selectedTier, setSelectedTier] = useState(params.get("tier") || "");
  const [selectedModel, setSelectedModel] = useState(params.get("model") || "");
  const [searchInput, setSearchInput] = useState(params.get("search") || "");
  const [search, setSearch] = useState(params.get("search") || "");
  const searchTimer = useRef(null);
  const badge = params.get("badge") || "";

  // Debounce search input → 300ms
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(searchTimer.current);
  }, [searchInput]);

  useEffect(() => {
    api.get("/vehicles/brands").then((r) => setBrands(r.data.brands || []));
    api.get("/vehicles/tiers").then((r) => setTiers(r.data || []));
  }, []);

  const query = useMemo(() => {
    const q = {};
    if (selectedBrand) q.brand = selectedBrand;
    else if (selectedTier) q.tier = selectedTier;
    if (selectedModel) q.model = selectedModel;
    if (priceMax && priceMax < 1200) q.max_price = priceMax;
    if (sort) q.sort = sort;
    if (badge) q.badge = badge;
    if (screens.length === 1) q.screen = screens[0];
    if (search) q.search = search;
    // Split accessories vs main catalog so cars/carplay units don't mix with cables/adapters.
    q.is_accessory = accessoriesOnly ? true : false;
    return q;
  }, [selectedBrand, selectedTier, selectedModel, priceMax, sort, badge, screens, search, accessoriesOnly]);

  useEffect(() => {
    setLoading(true);
    api.get("/products", { params: query }).then((r) => setProducts(r.data)).finally(() => setLoading(false));
    // Meta Pixel + CAPI: Search (fires only when the user has typed something).
    if (search && typeof window !== "undefined" && typeof window.audrivioTrack === "function") {
      window.audrivioTrack("Search", { search_string: search });
    }
  }, [query]); // eslint-disable-line

  const toggleScreen = (v) => setScreens((cur) => (cur.includes(v) ? cur.filter((s) => s !== v) : [...cur, v]));

  const updateUrl = () => {
    const p = new URLSearchParams();
    if (selectedBrand) p.set("brand", selectedBrand);
    if (selectedTier && !selectedBrand) p.set("tier", selectedTier);
    if (selectedModel) p.set("model", selectedModel);
    if (priceMax < 1200) p.set("max_price", String(priceMax));
    if (sort !== "popular") p.set("sort", sort);
    if (screens.length === 1) p.set("screen", screens[0]);
    if (badge) p.set("badge", badge);
    if (search) p.set("search", search);
    setParams(p);
  };
  useEffect(updateUrl, [selectedBrand, selectedTier, selectedModel, priceMax, sort, screens, search]); // eslint-disable-line

  // React to URL changes triggered by external navigations (e.g. mega-menu / global search)
  useEffect(() => {
    const urlBrand = params.get("brand") || "";
    const urlModel = params.get("model") || "";
    const urlSearch = params.get("search") || "";
    if (urlBrand !== selectedBrand) setSelectedBrand(urlBrand);
    if (urlModel !== selectedModel) setSelectedModel(urlModel);
    if (urlSearch !== search) { setSearch(urlSearch); setSearchInput(urlSearch); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // Clicking a brand overrides tier filtering (brand is more specific)
  const onSelectBrand = (b) => { setSelectedBrand(b); if (b) setSelectedTier(""); setSelectedModel(""); };
  const onSelectTier = (t) => { setSelectedTier(selectedTier === t ? "" : t); setSelectedBrand(""); setSelectedModel(""); };
  const clearModel = () => setSelectedModel("");

  const clearSearch = () => { setSearchInput(""); setSearch(""); };

  return (
    <div className="min-h-screen bg-xd-light">
      <SEO
        title={accessoriesOnly ? "Accessori auto: cavi, adattatori, montaggio" : "Catalogo autoradio Android Plug&Play"}
        description={accessoriesOnly ? "Sfoglia tutti gli accessori audrivio: cavi, adattatori, kit di montaggio e ricambi per la tua autoradio Android. Filtra per marca auto." : "Sfoglia il catalogo audrivio: autoradio Android dedicate per Fiat, VW, Audi, BMW, Mercedes e altre marche. Filtra per marca, modello, dimensione schermo e prezzo."}
        path={accessoriesOnly ? "/accessori" : "/catalogo"}
        lang={lang}
      />
      <Header />
      <section className="bg-xd-dark text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
          <div className="font-mono-tech text-xs uppercase tracking-widest text-xd-cyan">{accessoriesOnly ? "Accessori" : "Catalogo"}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold mt-2">{accessoriesOnly ? "Accessori & Ricambi" : "Autoradio Android Plug&Play"}</h1>
          <p className="text-slate-300 mt-2 text-sm max-w-2xl">{accessoriesOnly ? "Cavi, adattatori, kit di montaggio e ricambi. Filtra per marca auto." : "Filtri per marca, dimensione schermo e prezzo. Trova la radio perfetta per il tuo modello."}</p>

          {/* Search bar */}
          <div className="mt-6 max-w-2xl">
            <div className="relative group">
              <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-xd-cyan transition-colors" />
              <Input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Cerca per marca, modello o nome prodotto (es. Fiat 500, Golf 7, cornice…)"
                data-testid="catalog-search-input"
                className="h-12 pl-11 pr-11 bg-xd-dark2 border-xd-darkBorder text-white placeholder:text-slate-500 focus:border-xd-cyan focus:ring-1 focus:ring-xd-cyan/40 rounded-xl"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={clearSearch}
                  data-testid="catalog-search-clear"
                  aria-label="Cancella ricerca"
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {search && (
              <div className="mt-2 flex items-center gap-2 text-xs text-slate-400" data-testid="catalog-search-active">
                Risultati per <span className="font-mono-tech text-xd-cyan">&quot;{search}&quot;</span>
              </div>
            )}
            {selectedModel && (
              <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="catalog-active-model">
                <span className="font-mono-tech text-[10px] uppercase tracking-widest text-slate-400">Modello attivo</span>
                <button
                  type="button"
                  onClick={clearModel}
                  data-testid="clear-model-chip"
                  className="inline-flex items-center gap-2 bg-xd-cyan/10 border border-xd-cyan/40 text-xd-cyan hover:text-white hover:bg-xd-cyan/20 rounded-full px-3 py-1 text-xs font-medium transition-colors"
                >
                  {selectedBrand ? `${selectedBrand} · ` : ""}{selectedModel}
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      <div className="pt-6">
        <KlarnaBanner variant="hero" />
      </div>

      {/* Tier chips row */}
      {tiers.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4" data-testid="tier-chip-row">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono-tech text-[10px] uppercase tracking-widest text-xd-muted mr-1">Segmento</span>
            <button
              type="button"
              onClick={() => onSelectTier("")}
              data-testid="tier-chip-all"
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${!selectedTier && !selectedBrand ? "bg-xd-dark text-white border-xd-dark" : "border-xd-border text-xd-muted hover:border-xd-cyan bg-white"}`}
            >
              Tutti
            </button>
            {tiers.map((t) => (
              <button
                key={t.tier}
                type="button"
                onClick={() => onSelectTier(t.tier)}
                data-testid={`tier-chip-${t.tier}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${selectedTier === t.tier ? "bg-xd-cyan text-xd-dark border-xd-cyan" : "border-xd-border text-xd-text hover:border-xd-cyan bg-white"}`}
              >
                {t.label} <span className={`ml-1 font-mono-tech ${selectedTier === t.tier ? "text-xd-dark/70" : "text-xd-muted"}`}>{t.products_count}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid lg:grid-cols-[260px_1fr] gap-8">
        <aside className="lg:sticky lg:top-24 self-start bg-white border border-xd-border rounded-xl p-5 space-y-6" data-testid="catalog-filters">
          <div className="flex items-center gap-2 font-display font-semibold">
            <SlidersHorizontal className="w-4 h-4" /> Filtri
          </div>

          <div>
            <div className="font-mono-tech text-[10px] uppercase tracking-widest text-xd-muted mb-2">Marca auto</div>
            <select value={selectedBrand} onChange={(e) => onSelectBrand(e.target.value)} data-testid="filter-brand" className="w-full border border-xd-border rounded-md px-3 py-2 text-sm bg-white">
              <option value="">Tutte le marche</option>
              {brands.map((b) => (<option key={b} value={b}>{b}</option>))}
            </select>
            {selectedTier && !selectedBrand && (
              <div className="text-[11px] text-xd-muted mt-2 italic">Filtro tier attivo: <strong className="text-xd-cyanHover not-italic">{tiers.find((t) => t.tier === selectedTier)?.label}</strong></div>
            )}
          </div>

          <div>
            <div className="font-mono-tech text-[10px] uppercase tracking-widest text-xd-muted mb-2">Dimensione schermo</div>
            <div className="space-y-2 text-sm">
              {['7"', '9"'].map((s) => (
                <label key={s} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={screens.includes(s)} onCheckedChange={() => toggleScreen(s)} data-testid={`filter-screen-${s.replace('"','')}`} />
                  <span>{s}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono-tech text-[10px] uppercase tracking-widest text-xd-muted">Prezzo max</span>
              <span className="font-mono-tech text-xs font-semibold">€ {priceMax}</span>
            </div>
            <Slider defaultValue={[priceMax]} max={1200} min={100} step={20} onValueChange={(v) => setPriceMax(v[0])} data-testid="filter-price-slider" />
          </div>

          <button type="button" onClick={() => { setSelectedBrand(""); setSelectedTier(""); setSelectedModel(""); setPriceMax(1200); setSort("popular"); setScreens([]); clearSearch(); }} data-testid="reset-filters-btn" className="text-xs text-xd-muted hover:text-xd-orange underline underline-offset-2">
            Reset filtri
          </button>
        </aside>

        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-xd-muted"><span data-testid="catalog-count">{products.length}</span> prodotti</div>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="sort-select" className="w-[220px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="popular">Più popolari</SelectItem>
                <SelectItem value="price_asc">Prezzo crescente</SelectItem>
                <SelectItem value="price_desc">Prezzo decrescente</SelectItem>
                <SelectItem value="rating">Recensioni</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="text-center py-20 text-xd-muted text-sm">Caricamento…</div>
          ) : products.length === 0 ? (
            <div className="text-center py-20 border border-dashed border-xd-border rounded-xl">
              <Filter className="w-8 h-8 mx-auto text-xd-muted" />
              <div className="mt-3 font-display text-lg">Nessun prodotto corrisponde ai filtri</div>
              <div className="text-sm text-xd-muted mt-1">Prova a modificare marca o fascia di prezzo.</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {products.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
